import { CandidateDocument } from '../components/CandidateDocument';
import { CandidateMeetings } from '@/features/meetings/components/CandidateMeetings';
import { ApplicationsPanel } from '@/features/careers/components/ApplicationsPanel';

import {
  ArrowLeft,
  Building2,
  FileText,
  Mail,
  Pencil,
  ScrollText,
  UserRound,
} from 'lucide-react';
import { Link, useNavigate, useParams } from 'react-router-dom';

import { ApiError } from '@/api/client';
import type { CandidateRead } from '@/api/schema';
import { Alert } from '@/components/feedback/Alert';
import { Page, PageHeader, Panel } from '@/components/layout/Page';
import { Avatar } from '@/components/ui/Avatar';
import { Button } from '@/components/ui/Button';
import { EmptyState, ErrorState } from '@/components/ui/EmptyState';
import { Skeleton } from '@/components/ui/Skeleton';
import { useCurrentUser } from '@/features/auth/api';
import { JobProfilesPanel } from '@/features/careers/components/JobProfilesPanel';
import { useDateFormat } from '@/features/company/api';
import { formatDate, formatDateTime } from '@/lib/dates';
import { can } from '@/lib/permissions';

import { useCandidate } from '../api';
import { Fact, FactAssignee, FactText, Facts } from '../components/CandidateFacts';
import { PointsMeter } from '../components/PointsMeter';
import { ProgressRail } from '../components/ProgressRail';
import { BooleanFlag, DocumentationFlag, RsrFlag } from '../components/StateFlag';
import { FlystStageChip, PaymentChip, StatusChip } from '../components/StatusChip';
import { APPLYING_FOR_LABEL, SOURCE_LABEL } from '../labels';
import { railPosition } from '../stages';
import styles from './CandidateDetailPage.module.css';

/** A candidate that is not there, told apart from a request that went wrong. */
function isMissing(error: unknown): boolean {
  if (!(error instanceof ApiError)) return false;
  if (error.isNotFound) return true;
  // A path segment that is not a UUID never reaches the service: FastAPI rejects it
  // at the boundary, and the result is a 422 naming the path parameter. Without this
  // branch a mistyped address offers "try again", which would fail identically for
  // ever.
  return (
    error.isValidation && error.fieldErrors.some((entry) => entry.field === 'candidate_id')
  );
}

export function CandidateDetailPage() {
  const { id = '' } = useParams();
  const navigate = useNavigate();
  const dateFormat = useDateFormat();
  const { data: user } = useCurrentUser();

  const query = useCandidate(id);

  if (query.isPending) {
    return (
      <Page className={styles.page}>
        <PageHeader className={styles.header} title="Candidate" />
        <Panel>
          <Skeleton height={120} />
        </Panel>
        <Panel>
          <Skeleton height={240} />
        </Panel>
      </Page>
    );
  }

  if (query.isError) {
    return (
      <Page>
        <Panel>
          {isMissing(query.error) ? (
            <EmptyState
              icon={<UserRound size={21} />}
              title="That candidate does not exist"
              body="The record may have been removed, or the address may be wrong."
              action={{
                label: 'Back to all candidates',
                onClick: () => {
                  void navigate('/candidates');
                },
              }}
            />
          ) : (
            <ErrorState
              message={
                query.error instanceof ApiError ? query.error.message : 'That did not load.'
              }
              onRetry={() => {
                void query.refetch();
              }}
            />
          )}
        </Panel>
      </Page>
    );
  }

  const candidate: CandidateRead = query.data;
  const rail = railPosition(candidate);
  // What the five tracked chips need to fetch and date their own history (Phase 16).
  const history = { candidateId: candidate.id, dateFormat };
  const canEdit = can(user, 'candidates', 'edit');
  const canViewCareers = can(user, 'careers');
  const location = [candidate.city, candidate.country].filter(Boolean).join(', ');

  return (
    <Page className={styles.page}>
      <div>
        <Link to="/candidates" className={styles.back}>
          <ArrowLeft size={15} aria-hidden="true" />
          All candidates
        </Link>
        <PageHeader
          className={styles.header}
          title={candidate.full_name}
          description={`${candidate.reference} · received ${formatDate(candidate.received_on, dateFormat)}${
            location ? ` · ${location}` : ''
          }`}
          actions={
            canEdit ? (
              <Button
                icon={<Pencil size={15} />}
                onClick={() => {
                  void navigate(`/candidates/${candidate.id}/edit`);
                }}
              >
                Edit record
              </Button>
            ) : null
          }
        />
      </div>
      {/* Said in words above the rail, because the rail says it in colour alone. */}
      {rail.stopped === 'hold' ? (
        <Alert tone="warning">
          <b>This candidate is on hold.</b> Nothing moves until the hold is lifted. The rail
          below shows how far they had got.
        </Alert>
      ) : null}
      {rail.stopped === 'rejected' ? (
        <Alert tone="danger">
          <b>This candidate was rejected.</b> The rail below shows how far they got before the
          decision.
        </Alert>
      ) : null}

      <Panel>
        <div className={styles.identity}>
          <Avatar name={candidate.full_name} size={48} />
          <div className={styles.identityText}>
            <StatusChip status={candidate.status} history={history} />
          </div>
          <PointsMeter value={candidate.sponsor_points} />
        </div>
        <ProgressRail position={rail} />
      </Panel>

      <div className={styles.layout}>
        <div className={styles.column}>
          {canViewCareers ? (
            <ApplicationsPanel candidateId={candidate.id} dateFormat={dateFormat} />
          ) : null}
          <Panel title="Screening" icon={<ScrollText size={15} />}>
            <Facts>
              <Fact label="Flyst screening">
                <FlystStageChip stage={candidate.flyst_stage} history={history} />
              </Fact>
              <Fact label="RSR screening">
                <RsrFlag value={candidate.rsr_state} history={history} />
              </Fact>
              <Fact label="RSR scheduled">
                {formatDate(candidate.rsr_scheduled_on, dateFormat)}
              </Fact>
              <Fact label="Documentation">
                <DocumentationFlag value={candidate.documentation} history={history} />
              </Fact>
              <Fact label="Contacted">
                <BooleanFlag value={candidate.contacted} />
              </Fact>
              <Fact label="Details shared">
                <BooleanFlag value={candidate.details_shared} />
              </Fact>
              <Fact label="Follow-up done">
                <BooleanFlag value={candidate.followup_done} />
              </Fact>
              <Fact label="Sent back for">
                {candidate.rework_reasons.length > 0 ? (
                  candidate.rework_reasons.join(', ').replace(/_/g, ' ')
                ) : (
                  <span className={styles.none}>Nothing</span>
                )}
              </Fact>
            </Facts>
          </Panel>

          <Panel title="Application" icon={<Building2 size={15} />}>
            <Facts>
              <Fact label="Received">{formatDate(candidate.received_on, dateFormat)}</Fact>
              <Fact label="Source">{SOURCE_LABEL[candidate.source]}</Fact>
              <Fact label="Applying for">{APPLYING_FOR_LABEL[candidate.applying_for]}</Fact>
              <Fact label="Department">
                <FactText value={candidate.department?.name} />
              </Fact>
              <Fact label="Preferred position">
                <FactText value={candidate.preferred_position?.name} />
              </Fact>
              <Fact label="Preferred country">
                <FactText value={candidate.preferred_country?.name} />
              </Fact>
            </Facts>
          </Panel>

          <Panel title="Documents" icon={<FileText size={15} />}>
            <CandidateDocument
              label="CV"
              kind="cv"
              file={candidate.cv_file}
              candidateId={candidate.id}
              canEdit={canEdit}
            />
            <CandidateDocument
              label="Passport"
              kind="passport"
              canEdit={canEdit}
              file={candidate.passport_file}
              candidateId={candidate.id}
            />
          </Panel>
          {can(user, 'meetings') ? <CandidateMeetings candidate={candidate} /> : null}
        </div>

        <div className={styles.column}>
          {canViewCareers ? <JobProfilesPanel candidate={candidate} canSend={canEdit} /> : null}
          <Panel title="Contact" icon={<Mail size={15} />}>
            <Facts>
              <Fact label="Email">
                {candidate.email ? (
                  <a className={styles.contactLink} href={`mailto:${candidate.email}`}>
                    {candidate.email}
                  </a>
                ) : (
                  <FactText value={null} />
                )}
              </Fact>
              <Fact label="Phone">
                <FactText value={candidate.phone} />
              </Fact>
              <Fact label="Location">
                <FactText value={location} />
              </Fact>
            </Facts>
          </Panel>

          <Panel title="Payment" icon={<ScrollText size={15} />}>
            <PaymentChip status={candidate.payment_status} history={history} />
          </Panel>

          <Panel title="Record" icon={<UserRound size={15} />}>
            <Facts>
              <Fact label="Reference">{candidate.reference}</Fact>
              <Fact label="Assigned to">
                <FactAssignee assignee={candidate.assignee} />
              </Fact>
              <Fact label="Created">{formatDateTime(candidate.created_at, dateFormat)}</Fact>
              <Fact label="Last updated">
                {formatDateTime(candidate.updated_at, dateFormat)}
              </Fact>
            </Facts>
          </Panel>
        </div>
      </div>
    </Page>
  );
}
